import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";

/**Services**/
import {
  getAllDeviceList,
  performModeChange,
} from "../../services/DeviceService";
import { getLocationAndHierarchyDetails } from "../../services/LocationService";

/**Components**/
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import DropDown from "../../utils/components/common/dropdown/dropdown";
import GuageChart from "../../utils/components/common/guageChart";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import Modal from "../../utils/components/common/modal";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import Pagination from "../../utils/components/common/pagination";
import SearchBox from "../../utils/components/common/searchBox/searchBox";
import { useIsMount } from "../../utils/components/common/useIsMount";
import Icons from "../../utils/components/icons";
import ListView from "./ListView";
import DeviceFilter from "./deviceFilter/DeviceFilter";

/**Other Config/Utils/Constants**/
import { backendConfig } from "../../config/BackendConfig";
import {
  NotificationContext,
  OrganisationContext,
  UserContext,
  SortingContext,
} from "../../layouts/MainLayout";
import {
  DEVICE_MODES,
  getDeviceSize,
  getModeValue,
  modeLabels,
} from "../../utils/constants/deviceConstants";
import {
  USER_ROLES,
  updateCommonInfo,
  updateWebInfo,
} from "../../utils/constants/userConstants";
import {
  cartridgeColorFinder,
  formatAMPM,
  modeChecker,
  round5,
  round5ForCatridge,
  roundToTwoDec,
  valueFinderByModes,
} from "../../utils/utils";

/**Third-Party Components**/
import { PubSub } from "aws-amplify";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./dashboard.scss";

const filtersIcon = {
  clo2: Icons.ppbIcon,
  mode: Icons.nutIcon,
  deviceLocationHierarchy: Icons.locationIcon,
  cartridgeLevel: Icons.testTubeFilterIcon,
  temperature: Icons.barometerIcon,
  humidity: Icons.meterIcon,
};
let timeoutCall = null;

const sortingLables = [
  { label: "Events & Faults", value: "eventAndFaultSort" },
  {
    label: "Faults",
    value: "faultSort",
  },
  {
    label: "Device Name",
    value: "deviceNameSort",
  },
  {
    label: "High PPB Level",
    value: "clo2ValueSort",
  },
  {
    label: "Low Cartridge Level",
    value: "cartridgeLevelSort",
  },
];

const sortingPayloadLabels = [
  {
    eventAndFaultSort: "eventTimeStamp",
  },
  {
    faultSort: "faultEventTimeStamp",
  },
  {
    deviceNameSort: "DeviceName",
  },
  {
    clo2ValueSort: "clo2Value",
  },
  {
    cartridgeLevelSort: "cartridgeLevel",
  },
];

const Dashboard = ({
  globalDeviceDetails,
  globalLocationDetails,
  deviceQueryCount,
}) => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const isFirstMount = useIsMount();

  /**Context**/
  const {
    userData,
    userCognitoData,
    loggedInUserRole,
    featureEnabledStatus,
    isDashboardSorting,
    refreshRateDashboard,
    setRefreshRateDashboard,
  } = useContext(UserContext);
  const { siteData } = useContext(OrganisationContext);
  const { isNewMode, setIsNewMode } = useContext(NotificationContext);

  /**Refs**/
  const searchRef = useRef();
  const sortRef = useRef();
  const changeModeSub = useRef(null);
  const [isDeviceModeErrorModal, setIsDeviceModeErrorModal] = useState(false);
  const [listView, setListView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFilterErrorModal, setIsFilterErrorModal] = useState(false);
  const [isFilterApplyActioned, setIsFilterApplyActioned] = useState(false);

  const [pageCount, setPageCount] = useState(
    sessionStorage.getItem("pagination")
      ? Number(sessionStorage.getItem("pagination"))
      : 0
  );
  const [pageTotalCount, setPageTotalCount] = useState(0);
  const [totalDeviceCount, setTotalDeviceCount] = useState(0);
  const [filteredDeviceCount, setFilteredDeviceCount] = useState(0);
  const changeModeLoadingTimeoutRef = useRef(null);


  const [deviceList, setDeviceList] = useState();
  const [showDevices, setShowDevices] = useState(false);
  const [refreshTime, setRefreshTime] = useState(new Date());
  const [searchSugestion, setSearchSugestion] = useState([]);
  const [sortList, setSortList] = useState([{ key: "DeviceName", val: "asc" }]);
  const [locationHierarchy, setLocationHierarchy] = useState([]);
  const [filterApplied, setFilterApplied] = useState(
    localStorage.getItem("filters") ? true : false
  );
  const [schedulerDeviceList, setSchedulerDeviceList] = useState();
  const [allDeviceList, setAllDeviceList] = useState([]);
  const [selectedSort, setSelectedSort] = useState(
    localStorage.getItem("selectedSortKey")
      ? localStorage.getItem("selectedSortKey")
      : "eventAndFaultSort"
  );
  const [selectedSortLabel, setSelectedSortLabel] = useState("");

  let dashboardLoadingTimeout = null;
  const refreshIntervalRef = useRef(null);
  const [searchKeyAvailable, setSearchKeyAvailable] = useState(
    sessionStorage.getItem("searchKey") ? true : false
  );

  const isSearchKeyStored = useMemo(() => {
    return sessionStorage.getItem("searchKey") || "";
  }, []);

  /**Formatter Function Starts**/
  const getModeList = useCallback(() => {
    const modeList = [
      { label: "Standby", value: "STANDBY" },
      { label: "Sense Only", value: "SENSE_ONLY" },
      { label: "Operational", value: "OPERATIONAL" },
    ];
    if (featureEnabledStatus.researchMode) {
      modeList.push({ label: "Research", value: "RESEARCH" });
    }
    return modeList;
  }, [featureEnabledStatus.researchMode]);

  const offlineMode = [{ label: "Offline", value: "OFFLINE" }];

  const cartridgeColor = useCallback((level, mode) => {
    const roundedLevel = round5(level);
    if (mode === "OFFLINE") {
      return "#3C3C3C";
    }
    if (roundedLevel < 10) {
      return "#FF2B02";
    } else if (roundedLevel < 20) {
      return "#FFD012";
    } else {
      return "#07D95A";
    }
  }, []);

  const getDeviceLocation = useCallback((dLocation) => {
    if (!dLocation) return "NA";

    if (dLocation) {
      const deviceLocation = dLocation?.split("#");

      if (deviceLocation.length === 2) {
        // To Check if there is No Selected Location available
        return "";
      } else {
        const remainingLocation = deviceLocation.slice(2); // To ignore the Tenant and Site Id
        const joinedLocation = remainingLocation.join(", "); // To join the remaining parts
        return joinedLocation;
      }
    }
  }, []);

  const getLocationLevels = useCallback((levels, label) => {
    let level = [];
    levels.forEach((elem) => {
      level.push(elem.name);
    });
    return label + " - " + level.join(" / ");
  }, []);
  /**Formatter Function Ends**/

  const setDashboardLoading = () => {
    clearTimeout(dashboardLoadingTimeout);
    dashboardLoadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 15000);
  };

  const handleLoading = useCallback((loading) => {
    setIsLoading(loading);
  }, []);

  const setSorting = useCallback((list) => {
    setSortList(list);
  }, []);

  const updatePageCount = useCallback((val) => {
    setPageCount(val);
    sessionStorage.setItem("pagination", val);
  }, []);

  const navigateToDevice = useCallback(
    (url) => {
      navigate(`/device-view/${url}`);
    },
    [navigate]
  );

  const debounceCallBack = (callBackFunc) => {
    if (timeoutCall !== null) {
      clearTimeout(timeoutCall);
    }
    timeoutCall = setTimeout(() => {
      callBackFunc();
    }, 1000);
  };

  const handleSearchDevice = () => {
    const searchQuery = searchRef.current.getSearchText();

    const filteredList = [];

    allDeviceList.forEach((item) => {
      let deviceName = item?.DeviceName;

      const lowercaseDeviceName = deviceName.toLowerCase();
      const lowercaseSearchQuery = searchQuery.toLowerCase();

      if (lowercaseDeviceName.includes(lowercaseSearchQuery)) {
        filteredList.push(deviceName);
      }
    });

    if (filteredList.length === 0) {
      if (searchQuery.length > 1) {
        if (isFirstMount) return;
        debounceCallBack(() => {
          getDeviceList(false, false);
        });
      }
    } else {
      setSearchSugestion(filteredList);
    }

    if (searchQuery.length === 0) {
      setSearchSugestion([]);
    }
  };

  /**Location API */
  const getLocationHierarchyDetails = useCallback(() => {
    if (siteData?.id && userCognitoData?.username && userData?.OrganizationID) {
      const queryParams = {
        organizationId: userData?.OrganizationID,
        locationHierarchy: userData?.OrganizationID + "#" + siteData?.id,
        requestedByUserId: userCognitoData.username,
        getLocationDeletableStatus: true,
      };
      getLocationAndHierarchyDetails(queryParams)
        .then((resp) => {
          if (resp?.data?.Items?.Level1 && resp.data.Items.Level1.length > 0) {
            setLocationHierarchy(resp.data.Items.Level1);
          }
        })
        .catch((error) => {});
    }
  }, [siteData?.id, userCognitoData?.username, userData?.OrganizationID]);

  /**Location List API**/
  const getDeviceList = useCallback(
    async (
      isAutoRefresh = false,
      isSearchSuggestionActive = true,
      fromCheck = true
    ) => {
      try {
        const tenantId = siteData.id;
        if (!tenantId) return;
        !isAutoRefresh && isSearchSuggestionActive && setIsLoading(true);

        const searchQuery = searchRef.current.getSearchText();
        const isSearchEnable = searchRef.current.getIsSearchEnable();
        const filterList = localStorage.getItem("filters");
        const sortLocalKey = localStorage.getItem("selectedSortKey");

        clearTimeout(dashboardLoadingTimeout);

        /**Device size calculation as per screensize starts**/
        // const deviceQueryCount1 = getDeviceSize();

        /**Device size calculation as per screensize ends**/

        const queryParams = {
          tenantId: tenantId,
          from: fromCheck ? pageCount * deviceQueryCount : 0,
          getSchedulerStatus: true,
          size: deviceQueryCount,
        };

        if (!fromCheck) {
          setPageCount(0);
          sessionStorage.setItem("pagination", 0);
        }

        if (!isSearchSuggestionActive) {
          delete queryParams.size;
        }

        if (searchQuery.length > 0) {
          if (isSearchEnable) {
            queryParams.isSuggestion = isSearchEnable;
          }
          queryParams.search = searchQuery;
        }

        if (filterList !== null) {
          let filter = JSON.parse(filterList);
          queryParams.filters = {};
          filter.forEach((elem) => {
            if (elem.value !== null) {
              queryParams.filters[elem.key] = elem.value;
            }
          });
        }

        if (sortLocalKey) {
          queryParams.sort = [];
          const sortObject = {};

          const selectedLabel = sortingPayloadLabels.find(
            (label) => sortLocalKey in label
          );

          if (selectedLabel) {
            const selectedKey = Object.values(selectedLabel)[0];
            let order = "desc";

            if (
              selectedKey === "DeviceName" ||
              selectedKey === "cartridgeLevel"
            ) {
              order = "asc";
            }

            sortObject[selectedKey] = order;

            queryParams.sort.push(sortObject);

            queryParams.sort.push({ eventTimeStamp: "desc" });

            if (
              selectedKey === "eventTimeStamp" ||
              selectedKey === "DeviceName"
            ) {
              queryParams.sort.splice(1, 1);
            }
          }
        }

        setSearchSugestion([]);

        const response = await getAllDeviceList(queryParams);

        if (response?.data?.Items) {
          if (queryParams.isSuggestion) {
            setSearchSugestion(response.data.Items);
          } else {
            /**Add default mode [Offline] for no mode condition */
            const data = response.data.Items.map((data) => {
              return { ...data, mode: getModeValue(data?.mode) };
            });
            setDeviceList(data);
            setShowDevices(true);
            setTotalDeviceCount(response?.data?.OverallDevicesCount);
            setFilteredDeviceCount(response?.data?.TotalDevicesCount);

            setPageTotalCount(
              Math.ceil(response.data.TotalDevicesCount / deviceQueryCount)
            );
            setSchedulerDeviceList(response?.data?.SchedulerEnabledStatus);
          }
        } else {
          setDeviceList([]);
          setShowDevices(true);
          setTotalDeviceCount(0);
          setFilteredDeviceCount(0);
        }
        setRefreshTime(new Date());
      } catch (error) {
        setShowDevices(true);
        setDeviceList([]);
      }
      setIsLoading(false);
    },
    [dashboardLoadingTimeout, pageCount, siteData?.id, sortList]
  );

  /**Device List API for Search**/
  const getFullDeviceList = useCallback(
    async (isAutoRefresh = false) => {
      try {
        const sortColumn = [...sortList];
        const filterList = localStorage.getItem("filters");
        const tenantId = siteData.id;

        if (!tenantId) return;

        clearTimeout(dashboardLoadingTimeout);

        const queryParams = {
          tenantId: tenantId,
          getSchedulerStatus: true,
        };

        if (filterList !== null) {
          let filter = JSON.parse(filterList);
          queryParams.filters = {};
          filter.forEach((elem) => {
            if (elem.value !== null) {
              queryParams.filters[elem.key] = elem.value;
            }
          });
        }

        if (sortColumn.length > 0) {
          queryParams.sort = [];
          sortColumn.forEach((elem) => {
            if (elem.val !== "") {
              queryParams.sort.push({ [elem.key]: elem.val });
            }
          });
        }
        const response = await getAllDeviceList(queryParams);

        if (response?.data?.Items) {
          const data = response.data.Items.map((data) => {
            return { ...data, mode: getModeValue(data?.mode) };
          });

          setAllDeviceList(data);
        } else {
          setDeviceList([]);
          setShowDevices(true);
          setTotalDeviceCount(0);
          setFilteredDeviceCount(0);
        }
        setRefreshTime(new Date());
      } catch (error) {
        // To display error popup if it is a Error in Request after Clicking on Apply Button in Filter Screen
        if (isFilterApplyActioned === true) {
          setIsFilterErrorModal(true);
        } else {
          setShowDevices(true);
          setDeviceList([]);
        }
      }
    },
    [dashboardLoadingTimeout, siteData.id, sortList, isFilterApplyActioned]
  );

  const handleRefreshBtn = useCallback(() => {
    setIsFilterApplyActioned(false);
    clearInterval(refreshIntervalRef.current);
    getDeviceList();
    getFullDeviceList();
    refreshIntervalRef.current = setInterval(() => {
      getDeviceList(true);
      getFullDeviceList();
    }, refreshRateDashboard);
  }, [getDeviceList, getFullDeviceList, refreshRateDashboard]);

  /**Mode Change logic Starts**/

  const cancelChangeModeLoading = useCallback(() => {
    setIsDeviceModeErrorModal(true);
    setIsLoading(false);
  }, []);

  const setChangeModeLoading = () => {
    clearTimeout(changeModeLoadingTimeoutRef?.current);
    setIsLoading(true);
    changeModeLoadingTimeoutRef.current = setTimeout(cancelChangeModeLoading, 15000);
  };

  const changeDeviceMode = useCallback(
    async (mode, deviceDetails) => {
      setIsLoading(true);
      const modeVal = {
        STANDBY: "standBy",
        SENSE_ONLY: "senseOnly",
        OPERATIONAL: "operational",
        RESEARCH: "research",
      };
      const queryParams = {
        siteId: siteData.id,
        deviceId: deviceDetails.DeviceUUID,
        mode: modeVal[mode],
      };
      try {
        const response = await performModeChange(queryParams);
        if (response?.data?.statusCode === "SUCCESS") {
          setChangeModeLoading();
          const url = `chorus/${backendConfig.PUBSUB_ENV}/site/${siteData.id}/notification/events`;
          if (changeModeSub.current) {
            await changeModeSub.current.unsubscribe();
          }
          changeModeSub.current = PubSub.subscribe(url).subscribe({
            next: (data) => {
              clearTimeout(changeModeLoadingTimeoutRef?.current);
              let changedModeVal = data?.value?.mode;
              if (changedModeVal) {
                const modes = {
                  standBy: "STANDBY",
                  senseOnly: "SENSE_ONLY",
                  operational: "OPERATIONAL",
                  research: "RESEARCH",
                };
                deviceDetails.mode = getModeValue(modes[changedModeVal]);
              } else {
                setIsDeviceModeErrorModal(true);
              }
              setIsLoading(false);
            },
            error: (error) => {
              setIsDeviceModeErrorModal(true);
              setIsLoading(false);
            },
            close: () => console.log(""),
          });
        } else {
          setIsDeviceModeErrorModal(true);
          setIsLoading(false);
        }
      } catch (e) {
        setIsLoading(false);
        setIsDeviceModeErrorModal(true);
      }
    },
    [siteData?.id]
  );

  const handleViewToggle = () => {
    if (changeModeSub.current) {
      changeModeSub.current.unsubscribe();
    }
    setListView(!listView);
  };
  /**Mode Change logic Ends**/

  /**Filters Logic Starts */
  const [deviceFilterValues, setDeviceFilterValues] = useState(
    localStorage.getItem("filters")
      ? JSON.parse(localStorage.getItem("filters"))
      : []
  );

  /**On any change in deviceFilterValues from onFilterChange, removeFilter, removeAllFilters */
  useEffect(() => {
    if (isFirstMount) return;
    let isValApplied = false;
    if (deviceFilterValues?.length) {
      deviceFilterValues.forEach((item) => {
        if (item.value != null) {
          isValApplied = true;
        }
      });
      setFilterApplied(isValApplied);
      if (!isValApplied) {
        localStorage.removeItem("filters");
        setDeviceFilterValues([]);
      }
      setPageCount(0);
      sessionStorage.setItem("pagination", 0);
      updateWebInfo(userCognitoData?.username);
    }
  }, [deviceFilterValues, userCognitoData?.username]);

  const removeFilter = useCallback(
    (key, locationSelectedValue) => {
      const filters = [...deviceFilterValues];
      const index = deviceFilterValues.findIndex((item) => item.key === key);
      if (index > -1) {
        if (locationSelectedValue) {
          // Set customValue of Location Filter to an empty array in Local Storage
          filters[index].customValue = [];
        }

        filters[index].value = null;
      }
      localStorage.setItem("filters", JSON.stringify(filters));
      setDeviceFilterValues(filters);
    },
    [deviceFilterValues]
  );

  const removeAllFilters = useCallback(() => {
    const currentfilters = [...deviceFilterValues];
    currentfilters.forEach((item) => (item.value = null));
    localStorage.removeItem("filters");
    setDeviceFilterValues([]);
    updateWebInfo(userCognitoData?.username);
  }, [deviceFilterValues, userCognitoData?.username]);

  const getFilterValue = useCallback((val, key) => {
    if (key === "mode") {
      let list = [];
      val.split(",").forEach((elem) => {
        list.push(modeLabels[elem]);
      });
      return list.join(", ");
    }

    if (typeof val === "string") {
      return val.replaceAll(",", " / ");
    }
    return `${val.between[0]}-${val.between[1]}`;
  }, []);

  const getFilterLabel = useCallback((lbl, key) => {
    let label = lbl;
    if (key === "mode") {
      label = "Device Mode  -";
    }
    if (key === "temperature") {
      label = "Temp.";
    }
    if (key === "clo2") {
      label = "ClO2";
    }
    return label;
  }, []);

  const onFilterChange = useCallback((val) => {
    setDeviceFilterValues(val);
    localStorage.setItem("filters", JSON.stringify(val));
    setIsFilterApplyActioned(true);
  }, []);

  /**Filters Logic Ends */

  useEffect(() => {
    /**For First render, use GlobalDevice & Location Details else execute below logic for any dependency change**/
    const isFirstRenderCompleted = localStorage.getItem(
      "firstAppRenderCompleted"
    );
    if (isFirstRenderCompleted) {
      setDashboardLoading();
      handleRefreshBtn();
      getLocationHierarchyDetails();
      return () => {
        clearTimeout(changeModeLoadingTimeoutRef?.current);
        clearTimeout(dashboardLoadingTimeout);
        clearInterval(refreshIntervalRef.current);
        if (changeModeSub.current) {
          changeModeSub.current.unsubscribe();
        }
      };
    }
  }, [
    pageCount,
    siteData,
    refreshRateDashboard,
    deviceFilterValues,
    handleRefreshBtn,
    getLocationHierarchyDetails,
  ]);

  useEffect(() => {
    if (isNewMode) {
      clearTimeout(changeModeLoadingTimeoutRef?.current);
      /**When user change mode from other device then also update list**/
      getDeviceList(true);
      setIsNewMode(false);
    }
  }, [getDeviceList, isNewMode, setIsNewMode]);

  /**Seq-2: To call full list of devices for search**/
  useEffect(() => {
    const isFirstRenderCompleted = localStorage.getItem(
      "firstAppRenderCompleted"
    );
    if (!isFirstRenderCompleted) {
      getFullDeviceList();
    }
  }, [getFullDeviceList]);

  /**Seq-1: To update data from getDetails combined API */
  useEffect(() => {
    setIsLoading(true);
    const isFirstRenderCompleted = localStorage.getItem(
      "firstAppRenderCompleted"
    );
    /**Execute below logic for first APP load, and if  isFirstRenderCompleted==true then don't execute**/
    if (isFirstRenderCompleted) return;
    if (globalDeviceDetails) {
      /**Update Device Details**/
      if (globalDeviceDetails?.Items?.length) {
        const data = globalDeviceDetails.Items.map((data) => {
          return { ...data, mode: getModeValue(data?.mode) };
        });
        setDeviceList(data);
        setShowDevices(true);
        setTotalDeviceCount(globalDeviceDetails.OverallDevicesCount);
        setFilteredDeviceCount(globalDeviceDetails.TotalDevicesCount);
        setPageTotalCount(
          Math.ceil(globalDeviceDetails.TotalDevicesCount / deviceQueryCount)
        );
        setSchedulerDeviceList(globalDeviceDetails?.SchedulerEnabledStatus);
      } else {
        setDeviceList([]);
        setShowDevices(true);
        setTotalDeviceCount(0);
        setFilteredDeviceCount(0);
      }
      setRefreshTime(new Date());
    }
    /**Update Location Details**/
    if (globalLocationDetails) {
      if (globalLocationDetails?.Level1?.length) {
        setLocationHierarchy(globalLocationDetails.Level1);
      }
    }
    setIsLoading(false);
    setTimeout(() => {
      localStorage.setItem("firstAppRenderCompleted", true);
    }, [2000]);
  }, [globalDeviceDetails, globalLocationDetails]);

  useEffect(() => {
    if (!isFirstMount) {
      if (siteData?.manually) {
        setPageCount(0);
        sessionStorage.setItem("pagination", 0);
      }
    }
  }, [siteData]);

  const showErr = useMemo(() => {
    return !listView && !deviceList?.length && !isLoading && showDevices;
  }, [listView, deviceList, isLoading, showDevices]);

  const emptyMsg = useMemo(() => {
    if (!listView && !deviceList?.length && !isLoading) {
      if (filterApplied) {
        return {
          title: t("no_device_found"),
          desc: t("no_device_found_desc"),
          titleName: "noSearchedDeviceFound",
          descName: "noSearchedDeviceFound",
          showButton: true,
          BtnTitle: t("resetFilters"),
          BtnTitleId: "resetFilters",
          BtnIcon: Icons.resetFilterIcon,
          BtnCTA: () => {
            removeAllFilters();
          },
        };
      }
      if (searchRef?.current?.getSearchText() !== "" && !filterApplied) {
        return {
          title: t("no_device_found"),
          desc: t("no_device_found_desc"),
          titleName: "noSearchedDeviceFound",
          descName: "noSearchedDeviceFound",
          showButton: false,
          BtnTitle: "",
          BtnIcon: null,
          BtnCTA: () => {},
        };
      }
      if (
        searchRef?.current?.getSearchText() === "" &&
        searchRef?.current?.getIsSearchEnable() === false &&
        !filterApplied
      ) {
        return {
          title: t("no_device_onboarded"),
          desc: t("no_device_onboarded_desc"),
          titleName: "noDeviceOnboarded",
          descName: "noDeviceOnboarded",
          BtnTitle: "",
          BtnIcon: null,
          BtnCTA: () => {},
        };
      }
      return {
        title: t("no_device_found"),
        desc: t("no_device_found_desc"),
        BtnTitle: "",
        BtnIcon: null,
        BtnCTA: () => {},
      };
    } else {
      return {
        title: t("no_device_found"),
        desc: t("no_device_found_desc"),
        BtnTitle: "",
        BtnIcon: null,
        BtnCTA: () => {},
      };
    }
  }, [
    listView,
    deviceList?.length,
    isLoading,
    filterApplied,
    t,
    removeAllFilters,
  ]);

  const updateSortingDetailsInUser = useCallback(() => {
    try {
      const username = userCognitoData?.username;

      const deviceKey =
        userCognitoData?.signInUserSession?.accessToken?.payload?.device_key;

      const localStoredSortVal = localStorage.getItem("selectedSortKey");

      const value = {
        selectedSortOption: `${localStoredSortVal}`,
      };

      updateCommonInfo(username, [deviceKey], value);
    } catch (error) {
      console.log(error);
    }
  }, [userCognitoData]);

  const handleSortSelection = useCallback(
    (val) => {
      setSelectedSort(val);

      localStorage.setItem("selectedSortKey", val);

      updateSortingDetailsInUser();

      if (!isFirstMount) {
        getDeviceList();
      }
    },
    [getDeviceList, isFirstMount, updateSortingDetailsInUser]
  );

  useEffect(() => {
    if (!isFirstMount) {
      getDeviceList();
    }
  }, [isDashboardSorting]);

  useEffect(() => {
    if (selectedSort) {
      const label = sortingLables.find(
        (item) => item.value === selectedSort
      )?.label;
      setSelectedSortLabel(label);
    }
  }, [selectedSort]);

  const isSchdEnable = useCallback(
    (item) => {
      let schdStatus = item ? item?.scheduleStatus || "" : "";
      return (
        schdStatus.toLowerCase() === "enabled" &&
        featureEnabledStatus?.scheduler
      );
    },
    [featureEnabledStatus?.scheduler]
  );

  const handleRefreshSelection = useCallback(
    (val) => {
      setRefreshRateDashboard((prevRate) => {
        if (prevRate !== val) {
          return val;
        }
        return prevRate;
      });
      localStorage.setItem("refreshRateDashboard", val);
      updateWebInfo(userCognitoData?.username);
    },
    [setRefreshRateDashboard, userCognitoData]
  );

  return (
    <div className="bodyInner dashboard-dark-body-inner">
      {/* Main Section Starts */}
      <div className="bodyContainer dashboard-dark-body-container">
        <div
          className={`controllesWrapper controllesWrapperFixed fixSubHeader dashboard-dark-header dashboard unHide ${
            deviceFilterValues.length ? "filtersApplied" : ""
          }`}
        >
          <div id="devicecount" className="">
            {showDevices && (
              <span className="totalCount dashboard-dark-totalCount">
                {filterApplied || searchKeyAvailable
                  ? t("deviceCountHeadingWithFilters", {
                      filteredCount: `${filteredDeviceCount}`,
                      totalCount: `${totalDeviceCount}`,
                    })
                  : t("deviceCountHeading", { count: totalDeviceCount })}
              </span>
            )}
          </div>
          <div
            className={`d-flex align-items-center searchFilterContainer ${
              showDevices ? "" : "hidden"
            }`}
          >
            <SearchBox
              defaultVal={isSearchKeyStored}
              ref={searchRef}
              autoid="dasboard_device_search"
              options={searchSugestion}
              getOptionCallBack={(fromCheck) => {
                sessionStorage.setItem(
                  "searchKey",
                  searchRef?.current?.getSearchText()
                );
                if (searchRef?.current?.getSearchText()) {
                  setSearchKeyAvailable(true);
                } else {
                  setSearchKeyAvailable(false);
                }
                getDeviceList(false, true, fromCheck);
              }}
              getCallBackDeviceSearch={() => handleSearchDevice()}
              isSearchDeviceActive={true}
              callFrom="dashboard"
            />
            <div className="rightContainer">
              <button
                id="filter"
                type="button"
                className="roundBtn primary"
                onClick={() => setIsFilterModalOpen(true)}
              >
                {Icons.filterIcon}
                <span>{t("add_filters")}</span>
              </button>
              <DropDown
                options={sortingLables}
                wrapperClass="primary"
                autoid="sortingDropdown"
                value={selectedSort}
                onSelection={(val) => {
                  handleSortSelection(val);
                }}
                radioIcon="true"
                isSorting="true"
                startIcon={Icons.sortingFilterIcon}
                title={`Sorted by - ${selectedSortLabel}`}
                finalSortingKey={selectedSortLabel}
                showListTitle={true}
                parentClass="sorting-dropdown"
                customId="sorting-"
              />
              <DropDown
                options={[
                  { label: t("refresh_rate_label.1min"), value: 60 * 1000 },
                  {
                    label: t("refresh_rate_label.5min"),
                    value: 5 * 60 * 1000,
                  },
                  {
                    label: t("refresh_rate_label.10min"),
                    value: 10 * 60 * 1000,
                  },
                  {
                    label: t("refresh_rate_label.15min"),
                    value: 15 * 60 * 1000,
                  },
                ]}
                wrapperClass="primary"
                autoid="refresh"
                value={refreshRateDashboard}
                onSelection={(val) => {
                  handleRefreshSelection(val);
                }}
                parentClass="refresh-rate-dropdown"
              />
              <button
                id="refreshbtn"
                type="button"
                title="Refresh"
                className="verticalBtn"
                onClick={() => {
                  handleRefreshBtn();
                }}
              >
                {Icons.refreshIcon}
              </button>
              <button
                id="viewtoggle"
                type="button"
                title={`${listView ? "Grid View" : "List View"}`}
                className="verticalBtn"
                onClick={handleViewToggle}
              >
                {listView ? (
                  <span id="list_view_toggle">{Icons.gridIcon}</span>
                ) : (
                  <span id="grid_view_toggle">{Icons.listIcon}</span>
                )}
              </button>
            </div>
          </div>
        </div>
        {/* Filter Section Starts */}
        {deviceFilterValues?.length > 0 && showDevices && (
          <div className="selectedFiltersWrapper d-flex flex-wrap fixSubHeader dashboard">
            {deviceFilterValues.map((item, index) =>
              item.value !== null ? (
                <div
                  className={`filters ${item.key}`}
                  key={`selectedFilter_${index}`}
                  name={`filters ${item.key}`}
                >
                  {filtersIcon[item.key]}
                  <span id="filteredLabel">
                    {item.key === "deviceLocationHierarchy"
                      ? getLocationLevels(item.customValue, item.label)
                      : `${getFilterLabel(
                          item.label,
                          item.key
                        )}  ${getFilterValue(item.value, item.key)} ${
                          item.unit === "C" ? `\u00B0 ${item.unit}` : item.unit
                        }`}
                  </span>
                  <button
                    className="removeFilter"
                    onClick={
                      item.key === "deviceLocationHierarchy"
                        ? () => removeFilter(item.key, item.customValue)
                        : () => removeFilter(item.key)
                    }
                    type="button"
                    id={`removeFilter_${index}`}
                  >
                    {Icons.closeIcon}
                  </button>
                </div>
              ) : null
            )}
          </div>
        )}
        {/* Filter Section Ends */}

        {/* Device View Section Starts */}
        <div
          className={`gridViewWrapper device-container visibleOverflow ${
            deviceFilterValues?.length > 0 && showDevices && "increaseMargin"
          }`}
        >
          {showDevices ? (
            <>
              <div className="gridViewInner">
                {listView ? (
                  <ListView
                    list={deviceList}
                    isFilterApplied={filterApplied}
                    modeList={getModeList()}
                    sortCallBack={setSorting}
                    removeAllFilters={removeAllFilters}
                    sorting={sortList}
                    isLoading={isLoading}
                    ref={sortRef}
                    searchText={searchRef.current.getSearchText()}
                    handleLoading={handleLoading}
                    id="device_list_table"
                    schedulerDeviceList={schedulerDeviceList}
                  />
                ) : (
                  <div className="d-flex flex-wrap justify-content-start flex-grow gridContainer">
                    {deviceList.length > 0 &&
                      deviceList.map((item, index) => {
                        return item?.mode ? (
                          <div
                            className="position-relative card-container"
                            key={`device_card_${item?.DeviceUUID}_${index}`}
                          >
                            <div
                              // to={`/device-view/${item?.DeviceName}`}
                              onClick={() => navigateToDevice(item?.DeviceUUID)}
                              className={`card deviceCard  card_${index}`}
                              name={item?.DeviceUUID}
                            >
                              <div className="cardHeader">
                                <div
                                  className={`d-flex align-items-center deviceName ${item?.mode}`}
                                  title={`Device Name: ${item?.DeviceName}`}
                                >
                                  {item?.mode === "OFFLINE" ? (
                                    <>{Icons.offlineDeviceIcon}</>
                                  ) : (
                                    <>{Icons.deviceHorizontalIcon}</>
                                  )}
                                  <span className="ms-2">
                                    {item?.DeviceName}
                                  </span>
                                </div>
                                <div
                                  className={`modeContainer ${
                                    item?.mode === "OFFLINE"
                                      ? "offline-modeContainer"
                                      : "online-modeContainer"
                                  }`}
                                >
                                  {featureEnabledStatus?.ota &&
                                    loggedInUserRole !==
                                      USER_ROLES.viewOnlyUserCorp &&
                                    loggedInUserRole !==
                                      USER_ROLES.userCorp && (
                                      <span
                                        className={`deviceOta ${
                                          item?.mode === "OFFLINE"
                                            ? "offline"
                                            : "online"
                                        }`}
                                      >
                                        {item?.otaStatus === "InProgress" ? (
                                          <>{Icons.otaInProgressIcon}</>
                                        ) : item?.otaStatus ===
                                          "UpdateAvailable" ? (
                                          <>{Icons.otaAvailableIcon}</>
                                        ) : (
                                          <></>
                                        )}
                                      </span>
                                    )}

                                  {/* {featureEnabledStatus?.scheduler &&
                                    loggedInUserRole !==
                                      USER_ROLES.viewOnlyUserCorp &&
                                    loggedInUserRole !==
                                      USER_ROLES.userCorp && (
                                      <span
                                        className={`deviceEnabledScheduler ${
                                          item?.mode === "OFFLINE"
                                            ? "offline"
                                            : ""
                                        }`}
                                      >
                                        {item?.schedulerEnabledStatus && (
                                          <>
                                            {Icons.deviceSchedulerIsEnabledIcon}
                                          </>
                                        )}
                                      </span>
                                    )} */}

                                  {isSchdEnable(item) && (
                                    <span
                                      className={`deviceEnabledScheduler ${
                                        item?.mode === "OFFLINE"
                                          ? "offline"
                                          : ""
                                      }`}
                                    >
                                      {Icons.deviceSchedulerIsEnabledIcon}
                                    </span>
                                  )}

                                  <DropDown
                                    options={
                                      item?.mode === "OFFLINE"
                                        ? offlineMode
                                        : getModeList()
                                    }
                                    value={item?.mode}
                                    hideSelectedOption
                                    wrapperClass={
                                      item?.mode === "OFFLINE"
                                        ? "greyLight"
                                        : "secondary"
                                    }
                                    identifier={item}
                                    autoid={`device_modes_${index}`}
                                    onSelection={changeDeviceMode}
                                    name="changemode"
                                    alwaysShowIcon={true}
                                    disabled={
                                      loggedInUserRole ===
                                      USER_ROLES.viewOnlyUserCorp
                                    }
                                  />
                                </div>
                              </div>
                              <div className="cardBody">
                                <div className="deviceDetails">
                                  <div
                                    className={`deviceAddressWrapper ${item?.mode}`}
                                    title={`Device Location: ${getDeviceLocation(
                                      item?.DeviceLocation
                                    )}`}
                                  >
                                    {getDeviceLocation(item?.DeviceLocation)
                                      .length ? (
                                      <>
                                        {item?.mode === "OFFLINE" ? (
                                          <>{Icons.offlineLocationIcon}</>
                                        ) : (
                                          <>
                                            {" "}
                                            {Icons.locationIconDashboardCard}
                                          </>
                                        )}
                                        <span className="deviceAddress">
                                          {getDeviceLocation(
                                            item?.DeviceLocation
                                          )}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="deviceAddress empty"></span>
                                    )}
                                  </div>

                                  <div className="d-flex parameter-container">
                                    <div className="graph">
                                      {modeChecker(item?.mode) ? (
                                        <>
                                          <GuageChart
                                            id={`GuageChart_${item?.DeviceUUID}_${index}`}
                                            usedForDashboard={true}
                                            size="0.9"
                                            mode={item?.mode}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <GuageChart
                                            id={`GuageChart_${item?.DeviceUUID}_${index}`}
                                            targetValue={roundToTwoDec(
                                              item?.target
                                            )}
                                            additionalPercentage={30}
                                            maxAdditionalPercentage={50}
                                            usedForDashboard={true}
                                            size="0.9"
                                            value={roundToTwoDec(
                                              item?.clo2Value
                                            )}
                                          />
                                        </>
                                      )}
                                      <div className="graphReading" name="ppb">
                                        <div
                                          className={`d-flex justify-center align-items-center ${item?.mode} ppb`}
                                        >
                                          {/* {item?.mode === "OFFLINE" ? (
                                            <>{Icons.offlinePPBIcon}</>
                                          ) : (
                                            <> {Icons.ppbIcon}</>
                                          )} */}
                                          {Icons.ppbIcon}
                                          <span className="textPPB">
                                            {t("ppb")}
                                          </span>
                                        </div>
                                        <span className="ppb_value">
                                          {valueFinderByModes(
                                            item?.mode,
                                            "ppb",
                                            item?.clo2Value,
                                            false
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      className="mt-3 reading-container"
                                      style={{ width: "58%" }}
                                    >
                                      <div className="d-flex justify-content-between">
                                        <button
                                          type="button"
                                          name="temperature"
                                          className={`verticalBtn horizontal
                                            ${item?.mode}
                                            temperature`}
                                          title={`Device Temperature: ${valueFinderByModes(
                                            item?.mode,
                                            "temperature",
                                            item?.temperature
                                          )}`}
                                        >
                                          {item?.mode === "OFFLINE" ? (
                                            <>{Icons.offlineThermometerIcon}</>
                                          ) : (
                                            <> {Icons.barometerIcon}</>
                                          )}

                                          <span className="readingValue">
                                            <span className="reading1">
                                              {valueFinderByModes(
                                                item?.mode,
                                                "temperature",
                                                item?.temperature
                                              )}
                                            </span>
                                          </span>
                                        </button>
                                        <button
                                          type="button"
                                          name="humidity"
                                          className={`verticalBtn horizontal
                                            ${item?.mode}
                                            humidity`}
                                          title={`Device Humidity: ${valueFinderByModes(
                                            item?.mode,
                                            "humidity",
                                            item?.humidity
                                          )}`}
                                        >
                                          {item?.mode === "OFFLINE" ? (
                                            <>{Icons.offlineHumidityIcon}</>
                                          ) : (
                                            <> {Icons.dropPercentIcon}</>
                                          )}
                                          <span className="readingValue">
                                            <span className="reading1">
                                              {valueFinderByModes(
                                                item?.mode,
                                                "humidity",
                                                item?.humidity
                                              )}
                                            </span>
                                          </span>
                                        </button>
                                      </div>
                                      <div
                                        className={`verticalRangeWrapper mt-4 cartridge-icon ${item?.mode}`}
                                        id="cartridge_level_element"
                                        title={`Device Cartridge Level: ${valueFinderByModes(
                                          item?.mode,
                                          "cartridge",
                                          item?.cartridgeLevel
                                        )}`}
                                      >
                                        <div
                                          className="verticalRange"
                                          id="cartridge_level"
                                        >
                                          <span
                                            className="range"
                                            style={{
                                              background: cartridgeColor(
                                                round5ForCatridge(
                                                  item?.cartridgeLevel
                                                ),
                                                item?.mode
                                              ),
                                              width: `${
                                                round5ForCatridge(
                                                  item?.cartridgeLevel
                                                ) === 15
                                                  ? round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) - 5
                                                  : round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) === 20
                                                  ? round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) + 20
                                                  : round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) >= 20 &&
                                                    round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) <= 30
                                                  ? round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) + 15
                                                  : round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) === 35
                                                  ? round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    ) + 5
                                                  : round5ForCatridge(
                                                      item?.cartridgeLevel
                                                    )
                                              }%`,
                                            }}
                                          ></span>
                                          <span
                                            className={`rangeLabel ${cartridgeColorFinder(
                                              item?.cartridgeLevel,
                                              item?.mode,
                                              "dashboard"
                                            )}`}
                                          >
                                            {valueFinderByModes(
                                              "",
                                              "cartridge",
                                              item?.cartridgeLevel
                                            )}
                                          </span>
                                        </div>
                                        {item?.mode === "OFFLINE" ? (
                                          <>{Icons.offlineCartridgeIcon}</>
                                        ) : (
                                          <> {Icons.newTestTubeIcon}</>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              className="statusColorBar"
                              style={{
                                background:
                                  item?.mode && DEVICE_MODES[item?.mode].color,
                              }}
                            ></div>
                          </div>
                        ) : (
                          <></>
                        );
                      })}
                  </div>
                )}
              </div>
              {showErr ? (
                <div className="mb-5 w-100">
                  <EmptyScreen
                    img={Icons.noDeviceOnboarded}
                    {...emptyMsg}
                  ></EmptyScreen>
                </div>
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
        {/* Device View Section Ends */}

        {/* Footer Section Starts */}
        <div className="bodyFooter footerFixed dark-dashboard-footer">
          {deviceList && deviceList?.length ? (
            <Pagination
              currentPage={pageCount}
              totalPages={pageTotalCount}
              pageCallBack={updatePageCount}
            />
          ) : (
            <div></div>
          )}
          {showDevices && (
            <div id="lastrefresh" className="">
              Last refreshed on {formatAMPM(refreshTime, false)}
            </div>
          )}
        </div>
        {/* Footer Section Ends */}

        {/* Loader  starts*/}
        {isLoading && (
          <div className="dashboardLoader">
            <LoadingSpinner />
          </div>
        )}
        {/* Loader  Ends */}
      </div>
      {/* Main Section Ends */}

      {/* Modal Section Starts */}
      <Modal
        isOpen={isFilterModalOpen}
        onCloseModal={() => setIsFilterModalOpen(false)}
        className="deviceFilterWrapper"
        idName="deviceFilterModal"
      >
        <DeviceFilter
          onFilterChange={(val) => onFilterChange(val)}
          cancelFilter={() => setIsFilterModalOpen(false)}
          locationHierarchy={[...locationHierarchy]}
        />
      </Modal>

      <ModalComponent
        idName="dashboardmodal"
        isOpen={isDeviceModeErrorModal}
        onCloseModal={() => setIsDeviceModeErrorModal(false)}
        descriptionText={t("mode_change_error_message")}
      ></ModalComponent>

      <ModalComponent
        idName="filterErrormodal"
        isOpen={isFilterErrorModal}
        onCloseModal={() => setIsFilterErrorModal(false)}
        descriptionText={t("filterErrorMsg")}
      ></ModalComponent>

      {/* Modal Section Ends */}
    </div>
  );
};

export default Dashboard;

Dashboard.propTypes = {
  globalDeviceDetails: PropTypes.object,
  globalLocationDetails: PropTypes.object,
};
